import React from 'react';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';

import { useHistory } from 'react-router';
import { DSButton } from '../../components/Button/Button';
import { formValidators, getErrorMessage } from '../../helpers/formErrorMessages';
import AuthService from '../../services/AuthService';
import { RouterPaths } from '../../constants/routes';
import { getParameterByName } from '../../helpers/window-helper';
import { generateSignupUrl } from '../../services/RegistrationService';
import useStyles from './styles';

type SignupForm = {
  firstName: string;
  lastName: string;
  email: string;
  termsAgreed: boolean;
};

const getButtonText = (isSignup: boolean = false): string =>
  isSignup ? 'Complete Sign Up' : 'Next';

const SignUpForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupForm>();
  const history = useHistory();
  const classes = useStyles();

  const doSubmit = (data: SignupForm) => {
    window.sessionStorage.setItem('signup', JSON.stringify(data));

    const token = getParameterByName('token');
    if (token) AuthService.login(token);

    if (AuthService.loggedIn()) {
      history.replace(`${RouterPaths.authenticateSignup}?token=${token}`);
    } else {
      window.location.assign(generateSignupUrl(data.email));
    }
  };

  return (
    <form className={classes.signupForm} onSubmit={handleSubmit(doSubmit)}>
      <Grid container direction="column">
        <Grid item>
          <TextField
            inputProps={register('firstName', { required: true })}
            id="firstName"
            label="First Name"
            variant="outlined"
            className={classes.inputWrapper}
            error={!!errors.firstName}
            helperText={getErrorMessage(errors.firstName)}
            focused={false}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{ className: classes.input }}
            InputLabelProps={{ className: classes.inputLabel }}
          />
        </Grid>

        <Grid item>
          <TextField
            inputProps={register('lastName', { required: true })}
            id="lastName"
            label="Last Name"
            variant="outlined"
            className={classes.inputWrapper}
            error={!!errors.lastName}
            helperText={getErrorMessage(errors.lastName)}
            focused={false}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{ className: classes.input }}
            InputLabelProps={{ className: classes.inputLabel }}
          />
        </Grid>

        <Grid item>
          <TextField
            inputProps={register('email', { required: true, validate: formValidators.email })}
            id="email"
            label="Email Address"
            variant="outlined"
            className={classes.inputWrapper}
            error={!!errors.email}
            helperText={getErrorMessage(errors.email)}
            focused={false}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{ className: classes.input }}
            InputLabelProps={{ className: classes.inputLabel }}
          />
        </Grid>

        <Grid item className={classes.termsRow}>
          <label className="checkbox-container" htmlFor="termsAgreed">
            <Typography color="textPrimary">
              By pressing “Next” I agree to the{' '}
              <a
                href="https://www.dataswift.io/legal/eula"
                target="_blank"
                rel="noopener noreferrer"
              >
                Dataswift Developer’s Portal Terms of Services.
              </a>
            </Typography>
            <input
              id="termsAgreed"
              type="checkbox"
              {...register('termsAgreed', { required: true })}
            />
            <span className="checkbox-checkmark" />
          </label>
        </Grid>

        {errors.termsAgreed && (
          <Typography color="error">
            Please accept our terms and conditions before continuing
          </Typography>
        )}

        <Grid item xs={12} sm={3}>
          <DSButton type="submit">{getButtonText(getParameterByName('token') !== null)}</DSButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default SignUpForm;
